import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Popover } from "react-tiny-popover";

import { SPORTS_MENU } from "@/config/menus";
import { useAppContext } from "@/hooks/useAppContext";
import { useKeyPress } from "@/hooks/useKeyPress";
import caretDark from "@/public/caret-down-dark.svg";
import caretLight from "@/public/caret-down-light.svg";

const classNames = (...classes) => {
  return classes.filter(Boolean).join(" ");
};

const NewsPicksSelect = ({ leagueParam }) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [parentElement, setParentElement] = useState(null);
  const { newsPick, setNewsPick } = useAppContext();
  const league = router?.query?.league;
  const popoverParentId = "news-pick-select";

  useEffect(() => {
    // Set parent element to popover after javascript has been
    // hydrated on the client side and the document exists
    const popoverParentElement = document.getElementById(popoverParentId);
    if (popoverParentElement) {
      setParentElement(popoverParentElement);
    }
  }, [popoverParentId]);

  useEffect(() => {
    if (leagueParam === "soccer") {
      if (!newsPick) {
        setNewsPick(SPORTS_MENU["soccer"]);
      }
      if (league) {
        setNewsPick(SPORTS_MENU[league]);
      }
    }
  }, [leagueParam, newsPick, setNewsPick, league]);

  // Close popover if open and escape key is pressed
  useKeyPress("Escape", open, () => setOpen(false));

  // Set selected league and close popover
  const onSelect = (option) => {
    setOpen(false);
    if (leagueParam === "soccer") {
      setNewsPick(option);
      if (option?.name === "All Leagues") {
        delete router.query.league;
      } else {
        router.query.league = option?.slug;
      }
      router.replace(router);
    }
  };

  const MenuOptions = ({ divisionMenu }) =>
    Object.values(divisionMenu).map(
      (division, index) =>
        newsPick?.name !== division?.name && (
          <button
            key={`menu-item${index}`}
            onClick={() => onSelect(division)}
            className={classNames(
              "block w-full cursor-pointer px-4 py-2 text-left text-sm hover:bg-[#ccc]"
            )}
          >
            {division?.name}
          </button>
        )
    );

  const renderOptions =
    leagueParam === "soccer" ? (
      <MenuOptions divisionMenu={SPORTS_MENU} />
    ) : null;

  return (
    <div className="relative z-10 inline-block text-left" id={popoverParentId}>
      {parentElement && (
        <Popover
          isOpen={open}
          parentElement={parentElement}
          positions={["bottom"]}
          align="end"
          onClickOutside={() => setOpen(false)}
          content={
            <div className="ring-gray-300 mt-1 w-56 rounded-lg bg-white shadow-lg ring-1 ring-inset">
              <div autoFocus={open} className="py-1" id="division-menu">
                {renderOptions}
              </div>
            </div>
          }
        >
          <button
            className="text-gray-900 ring-gray-300 hover:bg-gray-50 inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset"
            onClick={() => setOpen((prevOpen) => !prevOpen)}
          >
            {leagueParam === "soccer" && newsPick?.name}
            <Image
              alt="Dropdown menu indicator for sports betting information"
              height={20}
              src={open ? caretDark : caretLight}
              width={20}
            />
          </button>
        </Popover>
      )}
    </div>
  );
};

export default NewsPicksSelect;
